import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Pencil, Save, X } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SocialMediaManager = () => {
  const [socialMedia, setSocialMedia] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSocialMedia();
  }, []);

  const fetchSocialMedia = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/social-media`);
      setSocialMedia(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching social media:', error);
      toast({
        title: "Error",
        description: "Failed to load social media",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleEdit = (social) => {
    setEditingId(social.social_id);
    setEditData({ ...social });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSave = async (socialId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/cms/social-media/${socialId}`, {
        name: editData.name,
        url: editData.url,
        icon: editData.icon
      });

      toast({
        title: "Success",
        description: "Social media updated successfully"
      });

      setEditingId(null);
      fetchSocialMedia();
    } catch (error) {
      console.error('Error updating social media:', error);
      toast({
        title: "Error",
        description: "Failed to update social media",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading social media...</div>;
  }

  return (
    <div>
      <h1 className="text-3xl font-light text-[#2d2d2d] mb-8">Social Media Management</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {socialMedia.map((social) => (
          <div key={social.social_id} className="bg-white p-6 rounded-lg shadow-lg">
            {editingId === social.social_id ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Platform Name</label>
                  <Input
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">URL</label>
                  <Input
                    value={editData.url}
                    onChange={(e) => setEditData({ ...editData, url: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Icon Name</label>
                  <Input
                    value={editData.icon}
                    onChange={(e) => setEditData({ ...editData, icon: e.target.value })}
                    placeholder="linkedin, instagram, twitter, facebook"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleSave(social.social_id)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                  <Button onClick={handleCancel} variant="outline">
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <h3 className="text-xl font-light text-[#2d2d2d] mb-2">{social.name}</h3>
                <p className="text-[#2d2d2d] font-light mb-2 break-all">{social.url}</p>
                <p className="text-sm text-[#2d2d2d] mb-4">Icon: {social.icon}</p>
                <Button
                  onClick={() => handleEdit(social)}
                  className="bg-[#0A1A50] hover:bg-[#060F2E] text-white w-full"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SocialMediaManager;